/**
 * Which rows of a quantity composition are held by value (MDOT-25244): a nested portfolio, an
 * index, a live proxy. None of them depends on a price, so neither the load nor an add waits on the
 * pricing engine to say so.
 *
 * - A loaded row says it itself: the backend writes a `quantity` on every row it holds in shares and
 *   none on a row it holds by value — the rule its pricing engine settles a held row by.
 * - An added row says it by its nature, by the same predicate the backend's `InstrumentHoldingKinds`
 *   applies — a nested portfolio, a live proxy, or a ticker named as an index — plus an instrument
 *   typed `INDEX`, which the frontend can read off the row where the backend reads only the name.
 */
import type { Instrument } from "../const";

const HELD_BY_VALUE_PROXY_TYPES: ReadonlySet<Instrument["proxyOverwriteType"]> = new Set<
	Instrument["proxyOverwriteType"]
>(["PORTFOLIO_MIXED", "LIVE", "HIDDEN_LIVE"]);

/** Whether an added row is held by value by its nature, whatever any price would say. */
export function isHeldByValueByNature(row: Instrument): boolean {
	if (row.isCash) {
		return false;
	}
	return (
		row.instrumentType === "INVESTMENT" ||
		row.type === "INDEX" ||
		HELD_BY_VALUE_PROXY_TYPES.has(row.proxyOverwriteType) ||
		(row.tickerName ?? "").toLowerCase().endsWith("index")
	);
}

/** Marks every loaded non-cash row that carries no `quantity` as held by value. Every other row is untouched. */
export function markHeldByValue(rows: Instrument[]): Instrument[] {
	return rows.map((row) => (!row.isCash && row.quantity == null ? { ...row, heldByValue: true } : row));
}
